import type { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { isStepLogicWorkspace, isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useUser } from '@r/api/auth.tsx';
import { useContext } from 'react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type {
  CompanyCompanyCollectionReadModel,
  GroupedWorkspaceConfigModel
} from '@r/api/generated/client.schemas.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { companyStatuses } from '@r/types/index.ts';
import { useApiCountriesGetCollection } from '@r/api/generated/client.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';
import type { ApiCountry } from '@ui/country-dropdown.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { canUseSalesFilter } from '@r/utils/roles.ts';
import { Badge } from '@r/components/ui/badge.tsx';

type Company = CompanyCompanyCollectionReadModel & {
  comment?: string | null;
};

interface UseCompanyFiltersProps {
  stepFilters: Record<string, string>;
  typologyFilters: Record<string, string>;
}

export const useFilterFields = ({
  stepFilters,
  typologyFilters
}: UseCompanyFiltersProps): DataTableFilterField<Company>[] => {
  const { t } = useTranslation();
  const stepFilterOptions = [];
  const typologyFilterOptions = [];

  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const { data: countriesResponse } = useApiCountriesGetCollection();
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
  const roles: string[] = (activeWorkspace?.roles as string[]) ?? [];
  const canUseSalesFilterValue = canUseSalesFilter(roles);

  if (stepFilters && Object.keys(stepFilters).length > 0) {
    for (const key in stepFilters) {
      stepFilterOptions.push({
        label: stepFilters[key],
        value: key
      });
    }
  }

  if (typologyFilters && Object.keys(typologyFilters).length > 0) {
    for (const key in typologyFilters) {
      typologyFilterOptions.push({
        label: typologyFilters[key],
        value: key
      });
    }
  }

  const statusOptions = companyStatuses.map((status) => ({
    label: t(status),
    value: status
  }));

  const countryOptions = (countriesResponse?.data as ApiCountry[] | undefined)?.map((country) => ({
    label: `${country.name} (${country.alpha3})`,
    value: country.alpha3
  }));

  return [
    {
      label: t('label.company.name'),
      value: 'name'
    },
    {
      label: t('label.company.unique.code'),
      value: 'uniqueCode'
    },
    {
      label: t('label.company.siren'),
      value: 'siren'
    },
    {
      label: t('label.company.tva'),
      value: 'tva'
    },
    {
      label: t('label.company.status'),
      value: 'status' as keyof Company,
      options: statusOptions
    },
    {
      label: t('label.company.address.line1'),
      value: 'addressLine1' as keyof Company
    },
    {
      label: t('label.company.zipcode'),
      value: 'zipcode'
    },
    {
      label: t('label.company.city'),
      value: 'city'
    },
    {
      label: t('label.company.country'),
      value: 'country.name',
      options: countryOptions ?? []
    },
    ...(canUseSalesFilterValue
      ? [
          {
            label: t('label.company.responsible'),
            value: 'commercial' as keyof Company
          }
        ]
      : []),
    {
      label: t('label.company.comment'),
      value: 'comment' as keyof Company
    },
    {
      label: `${t('point.name_other').charAt(0).toUpperCase() + t('point.name_other').slice(1)} ${t('common.acquired')}`,
      value: 'acquiredPoints' as keyof Company
    },
    {
      label: t('common.balance'),
      value: 'balance'
    },
    ...(stepFilterOptions.length > 0 && isStepLogicWorkspace(workspace)
      ? [
          {
            label: t('label.company.current.step'),
            value: 'lastStepReached' as keyof Company,
            options: stepFilterOptions
          }
        ]
      : []),
    ...(typologyFilterOptions.length > 0 && isTypologyConversion(workspaceConfigContext)
      ? [
          {
            label: t('label.company.typology'),
            value: 'typology' as keyof Company,
            options: typologyFilterOptions
          }
        ]
      : [
          {
            label: t('label.company.point.coefficient'),
            value: 'pointCoeff'
          }
        ]),
    {
      label: t('label.company.registered.at'),
      value: 'registeredAt' as keyof Company,
      isDateRange: true
    }
  ];
};

export const useCompanyColumns = (
  workspaceConfigContext?: GroupedWorkspaceConfigModel
): ColumnDef<Company>[] => {
  const { t } = useTranslation();
  const pointsName = t('point.name_other');

  const user = useUser().data;
  const { workspace } = UseWorkspace(user);

  return [
    {
      accessorKey: 'id',
      header: t('label.company.id')
    },
    {
      accessorKey: 'name',
      header: t('label.company.name')
    },
    {
      accessorKey: 'uniqueCode',
      header: t('label.company.unique.code')
    },
    {
      accessorKey: 'siren',
      header: t('label.company.siren')
    },
    {
      accessorKey: 'tva',
      header: t('label.company.tva')
    },
    {
      accessorKey: 'status',
      header: t('label.company.status'),
      cell: ({ row }) => (row.original.status ? t(row.original.status) : '')
    },
    {
      accessorKey: 'addressLine1',
      header: t('label.company.address.line1')
    },
    {
      accessorKey: 'zipcode',
      header: t('label.company.zipcode')
    },
    {
      accessorKey: 'city',
      header: t('label.company.city')
    },
    {
      id: 'country.name',
      accessorKey: 'country.name',
      header: t('label.company.country')
    },
    {
      id: 'commercial',
      accessorFn: (row) => row.responsibles?.[0]?.user.fullname ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.responsible')} />
      ),
      cell: ({ row }: CellContext<Company, unknown>) => {
        const responsibles = row.original.responsibles ?? [];
        const firstResponsible = responsibles[0];
        const remainingResponsiblesCount = Math.max(responsibles.length - 1, 0);

        if (!firstResponsible) {
          return '-';
        }

        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="min-w-0 truncate">
              <UserFullname
                fullname={firstResponsible.user.fullname ?? ''}
                isDeleted={firstResponsible.user.isDeleted}
              />
            </span>
            {remainingResponsiblesCount > 0 ? (
              <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-medium shrink-0">
                +{remainingResponsiblesCount}
              </Badge>
            ) : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'acquiredPoints',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={`${pointsName.charAt(0).toUpperCase() + pointsName.slice(1)} ${t('common.acquired')}`}
        />
      ),
      cell: ({ row }) => {
        return row.original.acquiredPoints.toLocaleString();
      }
    },
    {
      accessorKey: 'balance',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.balance')} />,
      cell: ({ row }) => {
        return row.original.balance.toLocaleString();
      }
    },
    ...(isStepLogicWorkspace(workspace)
      ? [
          {
            accessorKey: 'lastStepReached',
            header: ({ column }: HeaderContext<Company, unknown>) => (
              <DataTableColumnHeader column={column} title={t('label.company.current.step')} />
            ),
            cell: ({ row }: CellContext<Company, unknown>) => {
              return row.original.lastStepReached?.name;
            }
          }
        ]
      : []),
    ...(isTypologyConversion(workspaceConfigContext)
      ? [
          {
            accessorKey: 'typology',
            header: ({ column }: HeaderContext<Company, unknown>) => (
              <DataTableColumnHeader column={column} title={t('label.company.typology')} />
            ),
            cell: ({ row }: CellContext<Company, unknown>) => {
              return row.original.typology?.name;
            }
          }
        ]
      : [
          {
            accessorKey: 'pointCoeff',
            header: ({ column }: HeaderContext<Company, unknown>) => (
              <DataTableColumnHeader column={column} title={t('label.company.point.coefficient')} />
            ),
            cell: ({ row }: CellContext<Company, unknown>) => {
              return row.original.pointCoeff?.toLocaleString();
            }
          }
        ]),
    {
      accessorKey: 'comment',
      header: t('label.company.comment'),
      cell: ({ row }: CellContext<Company, unknown>) => (
        <span className="block max-w-[200px] truncate" title={row.original.comment ?? undefined}>
          {row.original.comment}
        </span>
      )
    },
    {
      accessorKey: 'registeredAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.registered.at')} />
      ),
      cell: ({ row }) => {
        return row.original.registeredAt
          ? format(new Date(row.original.registeredAt), 'dd/MM/yyyy', { locale: fr })
          : '';
      }
    }
  ];
};
